import { format, isValid, parse, startOfDay } from '@integrations/calendar'
import type { KeyboardEvent } from '@integrations/react'

/** The formats the shared date input (`mmfintech-portal-commons` BaseDateInput) accepts when a date is typed. */
const DATE_INPUT_FORMATS = ['dd/MM/yyyy', 'dd/MM/yy', 'dd.MM.yyyy', 'dd.MM.yy', 'yyyy-MM-dd'] as const

/**
 * Whether the typed text has the length of a finished date: dd/MM/yy (8) or dd/MM/yyyy (10, or ISO). Anything
 * shorter, and the 9 characters of a year still being typed, is not judged yet.
 */
export function isCompleteLength(text: string): boolean {
  return text.length === 8 || text.length >= 10
}

/**
 * The earliest date accepted. Without a lower bound a typed year such as 0020 is a valid date, and `fixDateOnly`
 * sends it to the backend as "20-10-10", which the backend cannot parse.
 */
export const DATE_INPUT_FLOOR = new Date(1970, 0, 1)

export type DateInputState = 'empty' | 'valid' | 'invalid'

/** A date only when the text is a complete date in one of the accepted formats (not "10/10/2" or "31/02/2026"). */
export function parseDateInput(text: string): Date | null {
  for (const dateFormat of DATE_INPUT_FORMATS) {
    const date = parse(text, dateFormat, new Date())
    if (isValid(date) && format(date, dateFormat) === text) return date
  }
  return null
}

/**
 * Whether typed text should be reported as an invalid date yet. Text that is not finished is not judged, and at
 * dd/MM/yy length only the date itself is judged, not the limits: the year may still get its other two digits
 * (01/01/20 is 2020, but 01/01/2019 is on its way).
 */
export function isTypedDateInvalid(text: string, min: Date, max: Date): boolean {
  if (!isCompleteLength(text)) return false
  if (text.length === 8) return parseDateInput(text) === null

  return getDateInputState(text, min, max) === 'invalid'
}

export function getDateInputState(text: string, min: Date, max: Date): DateInputState {
  if (!text.trim()) return 'empty'

  const date = parseDateInput(text)
  if (!date) return 'invalid'

  const day = startOfDay(date)
  return day < startOfDay(min) || day > startOfDay(max) ? 'invalid' : 'valid'
}

/** Lets only digits and the date separators be typed; navigation and shortcut keys pass. */
export function blockNonDateKey(event: KeyboardEvent<HTMLElement>) {
  if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey && !/[0-9./-]/.test(event.key)) {
    event.preventDefault()
  }
}

/**
 * "01011970" -> "01/01/1970": the slashes are added as the digits are typed, up to dd/MM/yyyy. Only digits and
 * slashes are reshaped; text with other separators (dotted or ISO dates) is left as typed.
 */
export function maskDateInput(text: string): string {
  if (!/^[\d/]*$/.test(text)) return text

  const digits = text.replace(/\D/g, '').slice(0, 8)
  return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4)].filter(Boolean).join('/')
}

/** Where the caret goes in the masked text so that it stays after the same number of digits as before. */
export function caretAfterDigits(masked: string, digitCount: number): number {
  if (digitCount <= 0) return 0

  let seen = 0
  for (let index = 0; index < masked.length; index++) {
    if (/\d/.test(masked[index]) && ++seen === digitCount) return index + 1
  }
  return masked.length
}

/**
 * Rewrites what was just typed into the input with the mask, keeping the caret where the user is typing. Meant to be
 * called from `onChangeRaw`: react-datepicker reads the input's value only after that callback.
 */
export function applyDateMask(input: HTMLInputElement) {
  const masked = maskDateInput(input.value)
  if (masked === input.value) return

  const digitsBeforeCaret = input.value.slice(0, input.selectionStart ?? input.value.length).replace(/\D/g, '').length
  const caret = caretAfterDigits(masked, digitsBeforeCaret)

  input.value = masked
  requestAnimationFrame(() => {
    if (document.activeElement === input) input.setSelectionRange(caret, caret)
  })
}
